import { useMemo } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend } from "recharts";
import "./AccelerometerCharts.css";

interface AccelerometerChartsProps {
  data: any[];
}

export default function AccelerometerCharts({ data }: AccelerometerChartsProps) {
  // Prepara i dati per i grafici
  const chartData = useMemo(() => {
    // Helper per trovare la chiave reale (gestisce varianti unicode / caratteri speciali)
    const findKey = (r: any, pattern: RegExp) => {
      const keys = Object.keys(r);
      const k = keys.find((kk) => pattern.test(kk));
      return k;
    };

    const getVal = (r: any, pattern: RegExp, fallback = 0) => {
      const k = findKey(r, pattern);
      if (!k) return fallback as number;
      const v = r[k];
      return typeof v === "number" ? v : (parseFloat(v) || fallback);
    };

    return data.map((row, index) => ({
      index: index + 1,
      time: row.Time || "",
      // Accelerazione (match case-insensitive, accept variations)
      accX: getVal(row, /accel(eration)?\s*x/i, 0),
      accY: getVal(row, /accel(eration)?\s*y/i, 0),
      accZ: getVal(row, /accel(eration)?\s*z/i, 0),
      // Velocità angolare
      gyroX: getVal(row, /angular\s*velocity\s*x|gyro.*x/i, 0),
      gyroY: getVal(row, /angular\s*velocity\s*y|gyro.*y/i, 0),
      gyroZ: getVal(row, /angular\s*velocity\s*z|gyro.*z/i, 0),
      // Angoli (roll/pitch/yaw)
      angleX: getVal(row, /angle\s*x|roll\s*\(?x\)?/i, 0),
      angleY: getVal(row, /angle\s*y|pitch\s*\(?y\)?/i, 0),
      angleZ: getVal(row, /angle\s*z|yaw\s*\(?z\)?/i, 0),
    }));
  }, [data]);

  // Calcola statistiche
  const stats = useMemo(() => {
    if (chartData.length === 0) {
      return {
        acceleration: { x: 0, y: 0, z: 0, magnitude: 0 },
        gyro: { x: 0, y: 0, z: 0 },
        angles: { x: 0, y: 0, z: 0 },
        temp: 0,
        battery: 0,
      };
    }

    const last = chartData[chartData.length - 1];
    const magnitude = Math.sqrt(
      last.accX * last.accX + last.accY * last.accY + last.accZ * last.accZ
    );

    return {
      acceleration: {
        x: last.accX,
        y: last.accY,
        z: last.accZ,
        magnitude: Number(magnitude.toFixed(3)),
      },
      gyro: {
        x: last.gyroX,
        y: last.gyroY,
        z: last.gyroZ,
      },
      angles: {
        x: last.angleX,
        y: last.angleY,
        z: last.angleZ,
      },
      // temp: last.temp,
      // battery: last.battery,
    };
  }, [chartData]);

  if (chartData.length === 0) {
    return (
      <div className="no-data-message">
        <p>📊 Nessun dato da visualizzare</p>
      </div>
    );
  }

  // Custom Tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">Campione #{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {Number(entry.value).toFixed(3)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="accelerometer-charts">
      {/* Statistiche Rapide */}
      <div className="stats-grid">
        <div className="stat-card accent">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <div className="stat-label">Accelerazione (g)</div>
            <div className="stat-value">{stats.acceleration.magnitude}</div>
            <div className="stat-detail">
              X: {stats.acceleration.x.toFixed(3)} | Y: {stats.acceleration.y.toFixed(3)} | Z:{" "}
              {stats.acceleration.z.toFixed(3)}
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">🔄</div>
          <div className="stat-content">
            <div className="stat-label">Velocità Angolare (°/s)</div>
            <div className="stat-value">
              {Math.max(Math.abs(stats.gyro.x), Math.abs(stats.gyro.y), Math.abs(stats.gyro.z)).toFixed(1)}
            </div>
            <div className="stat-detail">
              X: {stats.gyro.x.toFixed(1)} | Y: {stats.gyro.y.toFixed(1)} | Z: {stats.gyro.z.toFixed(1)}
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📐</div>
          <div className="stat-content">
            <div className="stat-label">Orientamento (°)</div>
            <div className="stat-value">{stats.angles.z.toFixed(1)}°</div>
            <div className="stat-detail">
              Roll: {stats.angles.x.toFixed(1)}° | Pitch: {stats.angles.y.toFixed(1)}°
            </div>
          </div>
        </div>
      </div>

      {/* Grafico Accelerazione */}
      <div className="chart-section">
        <div className="chart-header">
          <h3>📈 Accelerazione (g)</h3>
          <p className="chart-description">Accelerazione su 3 assi in unità g (gravità terrestre)</p>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#333" />
            <XAxis dataKey="index" stroke="#999" />
            <YAxis stroke="#999" domain={["auto", "auto"]} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line type="monotone" dataKey="accX" stroke="#ff6b6b" name="X" dot={false} strokeWidth={2} />
            <Line type="monotone" dataKey="accY" stroke="#4ecdc4" name="Y" dot={false} strokeWidth={2} />
            <Line type="monotone" dataKey="accZ" stroke="#ffe66d" name="Z" dot={false} strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Grafico Velocità Angolare */}
      <div className="chart-section">
        <div className="chart-header">
          <h3>🔄 Velocità Angolare (°/s)</h3>
          <p className="chart-description">Velocità di rotazione su 3 assi (giroscopio)</p>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#333" />
            <XAxis dataKey="index" stroke="#999" />
            <YAxis stroke="#999" domain={["auto", "auto"]} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line type="monotone" dataKey="gyroX" stroke="#ff6b6b" name="X" dot={false} strokeWidth={2} />
            <Line type="monotone" dataKey="gyroY" stroke="#4ecdc4" name="Y" dot={false} strokeWidth={2} />
            <Line type="monotone" dataKey="gyroZ" stroke="#ffe66d" name="Z" dot={false} strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Grafico Angoli (Orientamento) */}
      <div className="chart-section">
        <div className="chart-header">
          <h3>📐 Orientamento (°)</h3>
          <p className="chart-description">Angoli di orientamento: Roll (X), Pitch (Y), Yaw (Z)</p>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#333" />
            <XAxis dataKey="index" stroke="#999" />
            <YAxis stroke="#999" domain={["auto", "auto"]} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line type="monotone" dataKey="angleX" stroke="#ff6b6b" name="Roll (X)" dot={false} strokeWidth={2} />
            <Line type="monotone" dataKey="angleY" stroke="#4ecdc4" name="Pitch (Y)" dot={false} strokeWidth={2} />
            <Line type="monotone" dataKey="angleZ" stroke="#ffe66d" name="Yaw (Z)" dot={false} strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Info Dati */}
      <div className="data-info">
        <h3>ℹ️ Informazioni Dataset</h3>
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Campioni totali:</span>
            <span className="info-value">{chartData.length}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Primo timestamp:</span>
            <span className="info-value">{chartData[0]?.time || "N/A"}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Ultimo timestamp:</span>
            <span className="info-value">{chartData[chartData.length - 1]?.time || "N/A"}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
